import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { ApiService, FishStatResponse, NumFishStatsResponse } from './api.service';
import { db } from './db';
import { PhotoService } from './photo.service';
import { DeviceIdService } from './device-id.service';
import { Sentinels } from '@core/models/sentinels';
import { FishStat, MonthYearOption } from '@core/models/fish-stat.model';

/**
 * Utility: convert a Date or Date-like to a MySQL-style local datetime
 * string "YYYY-MM-DD HH:MM:SS". The iOS app sent this format and the
 * server parses it against the accompanying `timezone` field.
 */
function toMySqlDateTime(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function currentIanaTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Per-request timeout for the catch-download pipeline. Tuned to be
 * long enough for slow mobile connections to finish a large
 * get_by_id response (which carries a full base64 photo, often
 * 200-500KB), short enough that a stuck connection doesn't lock
 * the whole download for minutes.
 */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Per-IndexedDB-operation timeout. Reads and writes should be
 * sub-second under normal conditions. A multi-second wait means
 * something else is holding a transaction lock — most likely a
 * concurrent reference-data sync. We bail and continue so the
 * download can make progress; the worst case is the affected
 * record stays unfetched until the next download cycle.
 */
const DB_TIMEOUT_MS = 8_000;

/**
 * Wrap an arbitrary Promise with a timeout. Resolves with the
 * promise's value if it settles in time, rejects with an Error
 * tagged with the supplied label if it doesn't.
 *
 * Used for IndexedDB operations in the download loop. Dexie
 * promises don't support `.pipe(timeout())` like Observables do,
 * so we Promise.race against a setTimeout.
 */
function withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Timed out after ${ms}ms: ${label}`));
    }, ms);
    Promise.resolve(promise).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '';
  return String(v);
}

/**
 * Returns the string value if it's non-empty, else the supplied
 * sentinel. Used for downloading server records where missing fields
 * may come back as null, undefined, or empty string and we need to
 * collapse all three into "no data" rather than persisting empty
 * strings (which break the isPendingDate / isPendingString checks).
 */
function orSentinel(v: unknown, sentinel: string): string {
  const s = toStr(v);
  return s.length > 0 ? s : sentinel;
}

/**
 * Numeric counterpart to orSentinel — returns toNum(v) unless the
 * input was null/undefined/empty, in which case the supplied sentinel
 * (typically Sentinels.noDataDouble or noDataInt) is used. The plain
 * toNum() returns 0 for empty/null which would falsely look like a
 * legitimate "0 mph wind speed" reading.
 */
function orNumSentinel(v: unknown, sentinel: number): number {
  if (v === null || v === undefined) return sentinel;
  if (typeof v === 'string' && v.length === 0) return sentinel;
  return toNum(v);
}

/**
 * Pulls the singular record out of a fishStats response. The server
 * returns either `{fishStats: [{...}]}` (single-element array) or
 * occasionally `{fishStats: {...}}` directly. Returns null if the
 * shape doesn't match — caller should treat as "no record."
 */
function extractRecord(
  response: { fishStats?: unknown } | null | undefined,
): Record<string, unknown> | null {
  if (!response) return null;
  const fs = response.fishStats;
  if (!fs) return null;
  if (Array.isArray(fs)) {
    return (fs[0] as Record<string, unknown>) ?? null;
  }
  if (typeof fs === 'object') {
    return fs as Record<string, unknown>;
  }
  return null;
}

/**
 * Decode a base64 string into a Blob. Used for the image_data field
 * server records carry — iOS sent them as base64 JPEG, the server
 * stores and returns them in the same encoding.
 *
 * atob() is the only synchronous browser primitive for base64 decode;
 * we wrap the result in a Blob with the appropriate mime type.
 */
function base64ToBlob(b64: string, mimeType: string): Blob {
  // Strip a "data:image/jpeg;base64," prefix if present — defensive,
  // shouldn't happen for the API but doesn't hurt.
  const cleaned = b64.replace(/^data:[^;]+;base64,/, '');
  const binary = atob(cleaned);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

/**
 * Parameters for creating a new FishStat locally.
 * Matches the fields the Add Catch form collects.
 */
export interface NewCatchInput {
  lat: number;
  lon: number;
  date_and_time: Date;
  fish_type_id: number;
  fish_id: number;
  fish_length_id: number;
  fishing_technique_id: number;
  fishing_technique_other: string;
  photo: Blob | null;
}

/**
 * Orchestrates the catch lifecycle: create locally, POST to server,
 * merge server response back into local record, sync pending catches
 * when network returns.
 *
 * The My Catches list is always read from IndexedDB — the server is
 * treated as a write target and occasional refresher, not a read-time
 * dependency. This keeps the list instantly available offline and
 * avoids making users wait on a network round trip to see their own
 * data.
 */
@Injectable({ providedIn: 'root' })
export class CatchesService {
  private readonly api = inject(ApiService);
  private readonly photos = inject(PhotoService);
  private readonly deviceIdService = inject(DeviceIdService);

  /**
   * Creates a new catch in local storage, then attempts to POST it to
   * the server. If the network fails, the catch stays flagged for sync
   * and will retry on the next syncPending() call (triggered on
   * reconnect or manual refresh).
   *
   * Returns the locally-created FishStat (with server-populated fields
   * merged in if the initial POST succeeded).
   */
  async createCatch(
    userId: number,
    input: NewCatchInput,
  ): Promise<FishStat> {
    const deviceId = await this.deviceIdService.getOrCreate();
    const now = new Date();

    // Build the local record with sentinel values for all
    // server-populated fields. The iOS FishStat initializer does this
    // at construction time; we replicate it here as a helper.
    const stat: Omit<FishStat, 'id'> = {
      name: 'FishStat',
      is_update_needed: 1,
      last_update_failed: 0,
      user_id: userId,
      device_id: deviceId,
      image_name: '', // populated after Dexie assigns an id
      lat: input.lat,
      lon: input.lon,
      date_and_time: toMySqlDateTime(input.date_and_time),
      timezone: currentIanaTimezone(),
      fish_type_id: input.fish_type_id,
      fish_id: input.fish_id,
      fish_length_id: input.fish_length_id,
      fishing_technique_id: input.fishing_technique_id,
      fishing_technique_other: input.fishing_technique_other,
      remote_id: Sentinels.pendingId,
      moonrise: Sentinels.pendingDate,
      moonset: Sentinels.pendingDate,
      sunrise: Sentinels.pendingDate,
      sunset: Sentinels.pendingDate,
      moon_phase: Sentinels.pendingString,
      moon_percent_illuminated: Sentinels.pendingDouble,
      moon_position: Sentinels.pendingDouble,
      major_feed_time_start: Sentinels.pendingDate,
      major_feed_time_end: Sentinels.pendingDate,
      major_feed_time_start2: Sentinels.pendingDate,
      major_feed_time_end2: Sentinels.pendingDate,
      minor_feed_time_start: Sentinels.pendingDate,
      minor_feed_time_end: Sentinels.pendingDate,
      minor_feed_time_start2: Sentinels.pendingDate,
      minor_feed_time_end2: Sentinels.pendingDate,
      noaa_station_name: Sentinels.pendingString,
      noaa_station_number: Sentinels.pendingInt,
      noaa_station_distance: Sentinels.pendingDouble,
      low_tide_time: Sentinels.pendingDate,
      low_tide_level: Sentinels.pendingDouble,
      low_tide_time2: Sentinels.pendingDate,
      low_tide_level2: Sentinels.pendingDouble,
      high_tide_time: Sentinels.pendingDate,
      high_tide_level: Sentinels.pendingDouble,
      high_tide_time2: Sentinels.pendingDate,
      high_tide_level2: Sentinels.pendingDouble,
      water_level: Sentinels.pendingDouble,
      water_temp: Sentinels.pendingDouble,
      air_temp: Sentinels.pendingDouble,
      air_pressure: Sentinels.pendingDouble,
      wind_speed: Sentinels.pendingDouble,
      wind_gusts: Sentinels.pendingDouble,
      wind_direction: Sentinels.pendingString,
    };

    const newId = (await db.fish_stats.add(stat as FishStat)) as number;

    if (input.photo) {
      await db.fish_stat_images.put({
        fish_stat_id: newId,
        mime_type: input.photo.type || 'image/jpeg',
        data: input.photo,
      });
      await db.fish_stats.update(newId, {
        image_name: `MyFish_${newId}.jpg`,
      });
    }

    const fullRecord = (await db.fish_stats.get(newId)) as FishStat;

    // Attempt to sync immediately. Failure is non-fatal — the record
    // stays in local storage with is_update_needed=1 and will retry
    // on the next syncPending() call.
    await this.syncOne(fullRecord).catch((err) =>
      console.warn('Initial sync failed', err),
    );

    return (await db.fish_stats.get(newId)) as FishStat;
  }

  /**
   * Returns the user's catches for a given month+year, most recent
   * first. Reads entirely from IndexedDB — no network required.
   */
  async getCatchesForMonth(
    userId: number,
    month: number,
    year: number,
  ): Promise<FishStat[]> {
    // date_and_time stored as "YYYY-MM-DD HH:MM:SS" so we filter
    // lexically — fast and index-friendly.
    const prefix = `${year}-${String(month).padStart(2, '0')}`;
    const all = await db.fish_stats
      .where('user_id')
      .equals(userId)
      .toArray();
    return all
      .filter((s) => s.date_and_time.startsWith(prefix))
      .sort((a, b) => (a.date_and_time < b.date_and_time ? 1 : -1));
  }

  /**
   * Returns every catch the user has logged. Used by the Wizard tab
   * to drop pins on a map regardless of date — the visualization is
   * "all my fishing history." Sorted newest-first to match list views.
   */
  async getAllCatches(userId: number): Promise<FishStat[]> {
    const all = await db.fish_stats
      .where('user_id')
      .equals(userId)
      .toArray();
    return all.sort((a, b) =>
      a.date_and_time < b.date_and_time ? 1 : -1,
    );
  }

  /**
   * How many catches are flagged as needing server sync. Used by the
   * More page to surface the sync queue size, and to enable/disable
   * a manual Retry button when there's nothing to retry.
   */
  async countPendingSync(): Promise<number> {
    return db.fish_stats.where('is_update_needed').equals(1).count();
  }

  /**
   * Number of catches the server reports for this user. Cheap (1
   * request), used for the "is local stale?" check on app startup
   * and visibility-regain. Returns null on network error so the
   * caller can decide to retry vs. silently move on.
   */
  async countServerCatches(userId: number): Promise<number | null> {
    try {
      const deviceId = await this.deviceIdService.getOrCreate();
      const response = await firstValueFrom(
        this.api.getNumFishStats(userId, deviceId),
      );
      // Server wraps the count in a `fishStats` envelope:
      //   { fishStats: { num_fish_stats: "69" } }
      const n = toNum(response?.fishStats?.num_fish_stats);
      return Number.isFinite(n) ? n : 0;
    } catch (err) {
      console.warn('countServerCatches failed', err);
      return null;
    }
  }

  /**
   * Local count of catches for a user. Used alongside
   * countServerCatches to decide whether a refresh is worthwhile.
   */
  async countLocalCatches(userId: number): Promise<number> {
    return db.fish_stats.where('user_id').equals(userId).count();
  }

  /**
   * Downloads the user's existing catches from the server into local
   * storage. Used after a successful login so the user's history from
   * other devices (or from the iOS app) is available locally.
   *
   * Strategy mirrors the iOS app's `makeDatabaseCurrent` pipeline:
   *   1. Ask the server how many catches the user has (count).
   *   2. Walk the catch ids in order via repeated `get_next_id` calls.
   *   3. For each id, fetch the full record via `get_by_id`. Photo
   *      data comes back inline as base64 in `image_data`.
   *
   * That's a 2N+1 request pattern for N catches. Slow but unavoidable
   * — the server doesn't expose a bulk endpoint, and changing that
   * is out of scope for the port.
   *
   * Skips records that already exist locally (matched by remote_id).
   * This handles the common case of re-login on a device that has
   * already seen those catches; we don't waste bandwidth re-fetching.
   *
   * Reports progress through the optional callback so a UI can show
   * "downloading 7 of 23" while it works.
   */
  async downloadFromServer(
    userId: number,
    onProgress?: (done: number, total: number) => void,
  ): Promise<{ added: number; skipped: number; total: number }> {
    const deviceId = await this.deviceIdService.getOrCreate();
    console.log('[downloadFromServer] starting', { userId, deviceId });

    // Step 1: count. Server response is wrapped:
    //   { fishStats: { num_fish_stats: "69" } }
    let countResponse: NumFishStatsResponse | null = null;
    try {
      countResponse = await firstValueFrom(
        this.api
          .getNumFishStats(userId, deviceId)
          .pipe(timeout(REQUEST_TIMEOUT_MS)),
      );
      console.log('[downloadFromServer] count response', countResponse);
    } catch (err) {
      if (err instanceof TimeoutError) {
        console.error('[downloadFromServer] get_num_fish_stats timed out');
      } else {
        console.error('[downloadFromServer] get_num_fish_stats failed', err);
      }
      onProgress?.(0, 0);
      return { added: 0, skipped: 0, total: 0 };
    }
    const total = toNum(countResponse?.fishStats?.num_fish_stats);
    if (!Number.isFinite(total) || total <= 0) {
      console.log('[downloadFromServer] count is 0 or invalid; bailing', { total });
      onProgress?.(0, 0);
      return { added: 0, skipped: 0, total: 0 };
    }
    console.log(`[downloadFromServer] server reports ${total} catch(es)`);

    // Step 2/3: walk ids and fetch records
    let added = 0;
    let skipped = 0;
    // iOS uses -1 as the "start of walk" sentinel for the first
    // get_next_id call (SaltwaterUpDb.m line 127). The server's
    // CakePHP controller validates this param with PHP's empty(),
    // which considers "0" empty and returns a 404 with the message
    // "Invalid query. Parameter: remote_id required." Using -1
    // sidesteps this — empty(-1) is false. After the first response,
    // we use the returned id, which is always >= 1 for real records.
    let lastRemoteId = -1;

    onProgress?.(0, total);

    // Cap at 'total' iterations as a safety belt — the server should
    // return null/0 once we're past the end, but we don't want a
    // bug in the server response shape to cause an infinite loop.
    // We also bail early if we get the same id twice in a row.
    for (let i = 0; i < total; i++) {
      let nextResponse;
      try {
        // 30s timeout on the network call so a stalled connection
        // (mobile network drop, server hang) doesn't hang the whole
        // download. The loop continues to the next iteration on
        // failure rather than aborting entirely — one stuck record
        // shouldn't prevent the rest from arriving.
        nextResponse = await firstValueFrom(
          this.api
            .getNextFishStatId(userId, deviceId, lastRemoteId)
            .pipe(timeout(REQUEST_TIMEOUT_MS)),
        );
        if (i === 0) {
          // Log the first response so we can verify the shape if
          // something goes wrong. Subsequent ones are noisy.
          console.log('[downloadFromServer] first get_next_id response', nextResponse);
        }
      } catch (err) {
        if (err instanceof TimeoutError) {
          console.warn('[downloadFromServer] get_next_id timed out', { lastRemoteId });
        } else {
          console.warn('[downloadFromServer] get_next_id failed', { lastRemoteId, err });
        }
        // Bail — without a next id, we can't continue the walk anyway.
        break;
      }

      const nextRecord = extractRecord(nextResponse);
      if (!nextRecord) {
        console.log('[downloadFromServer] no more records (extractRecord returned null)', { i, lastRemoteId, nextResponse });
        break;
      }
      const nextId = toNum(nextRecord['id']);
      if (!Number.isFinite(nextId) || nextId <= 0 || nextId === lastRemoteId) {
        console.log('[downloadFromServer] no more records (invalid/duplicate id)', { i, nextId, lastRemoteId });
        break;
      }
      lastRemoteId = nextId;

      // Skip if we already have this catch locally — the user's
      // re-logging in on a device that's seen this record before.
      // Wrap with a timeout because IndexedDB operations CAN hang
      // when a write transaction is held elsewhere (e.g. concurrent
      // reference data sync). Falling through assumes "not present"
      // — worst case we re-fetch, which is cheap.
      let existing;
      try {
        existing = await withTimeout(
          db.fish_stats.where('remote_id').equals(nextId).first(),
          DB_TIMEOUT_MS,
          `existence-check remote_id=${nextId}`,
        );
      } catch (err) {
        console.warn('[downloadFromServer] existence check timed out', { nextId, err });
        existing = undefined;
      }
      if (existing) {
        skipped++;
        onProgress?.(i + 1, total);
        continue;
      }

      // Fetch the full record + photo. Same timeout treatment.
      let detailResponse;
      try {
        detailResponse = await firstValueFrom(
          this.api
            .getFishStatById(userId, deviceId, nextId)
            .pipe(timeout(REQUEST_TIMEOUT_MS)),
        );
      } catch (err) {
        if (err instanceof TimeoutError) {
          console.warn('[downloadFromServer] get_by_id timed out', { nextId });
        } else {
          console.warn('[downloadFromServer] get_by_id failed', { nextId, err });
        }
        onProgress?.(i + 1, total);
        continue;
      }

      const record = extractRecord(detailResponse);
      if (!record) {
        onProgress?.(i + 1, total);
        continue;
      }

      try {
        await withTimeout(
          this.persistDownloadedCatch(userId, deviceId, record),
          DB_TIMEOUT_MS,
          `persist remote_id=${nextId}`,
        );
        added++;
      } catch (err) {
        console.warn('[downloadFromServer] persist failed', { nextId, err });
      }

      onProgress?.(i + 1, total);
    }

    console.log('[downloadFromServer] complete', { added, skipped, total });
    return { added, skipped, total };
  }

  /**
   * Saves a server-fetched FishStat record into local storage. Builds
   * a complete local FishStat from the response with no pending
   * sentinels (this came from the server, so it's the source of truth)
   * and decodes any inline image_data into a Blob in fish_stat_images.
   */
  private async persistDownloadedCatch(
    userId: number,
    deviceId: string,
    record: Record<string, unknown>,
  ): Promise<void> {
    const remoteId = toNum(record['id']);

    // The server's get_by_id response only sends `image_data` (the
    // base64 photo blob) when one exists; it does NOT send a separate
    // `image_name` field. So we synthesize a stable name here whenever
    // image_data is present. The actual filename is only used as a
    // "this catch has a photo" boolean signal in the rest of the app
    // — it's not used to look the photo up in storage (that's keyed
    // by the local fish_stat_id).
    const imageData = toStr(record['image_data']);
    const hasPhoto = imageData.length > 0;
    const imageName = hasPhoto ? `server-${remoteId}.jpg` : '';

    const stat: Omit<FishStat, 'id'> = {
      name: toStr(record['name']) || 'FishStat',
      // is_update_needed=0 because this came FROM the server. Any
      // future edit will flip it to 1 and trigger a sync as usual.
      is_update_needed: 0,
      last_update_failed: 0,
      user_id: userId,
      device_id: deviceId,
      image_name: imageName,
      lat: toNum(record['lat']),
      lon: toNum(record['lon']),
      date_and_time: toStr(record['date_and_time']),
      timezone: toStr(record['timezone']),
      fish_type_id: toNum(record['fish_type_id']),
      fish_id: toNum(record['fish_id']),
      fish_length_id: toNum(record['fish_length_id']),
      fishing_technique_id: toNum(record['fishing_technique_id']),
      fishing_technique_other: toStr(record['fishing_technique_other']),
      remote_id: remoteId,

      // NOAA-populated fields. Use server-supplied values; fall back
      // to no-data sentinels if a field is missing or empty.
      moonrise: orSentinel(record['moonrise'], Sentinels.noDataDate),
      moonset: orSentinel(record['moonset'], Sentinels.noDataDate),
      sunrise: orSentinel(record['sunrise'], Sentinels.noDataDate),
      sunset: orSentinel(record['sunset'], Sentinels.noDataDate),
      moon_phase: orSentinel(record['moon_phase'], Sentinels.noDataString),
      moon_percent_illuminated: orNumSentinel(
        record['moon_percent_illuminated'],
        Sentinels.noDataDouble,
      ),
      moon_position: orNumSentinel(record['moon_position'], Sentinels.noDataDouble),
      major_feed_time_start: orSentinel(
        record['major_feed_time_start'],
        Sentinels.noDataDate,
      ),
      major_feed_time_end: orSentinel(
        record['major_feed_time_end'],
        Sentinels.noDataDate,
      ),
      major_feed_time_start2: orSentinel(
        record['major_feed_time_start2'],
        Sentinels.noDataDate,
      ),
      major_feed_time_end2: orSentinel(
        record['major_feed_time_end2'],
        Sentinels.noDataDate,
      ),
      minor_feed_time_start: orSentinel(
        record['minor_feed_time_start'],
        Sentinels.noDataDate,
      ),
      minor_feed_time_end: orSentinel(
        record['minor_feed_time_end'],
        Sentinels.noDataDate,
      ),
      minor_feed_time_start2: orSentinel(
        record['minor_feed_time_start2'],
        Sentinels.noDataDate,
      ),
      minor_feed_time_end2: orSentinel(
        record['minor_feed_time_end2'],
        Sentinels.noDataDate,
      ),
      noaa_station_name: orSentinel(
        record['noaa_station_name'],
        Sentinels.noDataString,
      ),
      noaa_station_number: orNumSentinel(
        record['noaa_station_number'],
        Sentinels.noDataInt,
      ),
      noaa_station_distance: orNumSentinel(
        record['noaa_station_distance'],
        Sentinels.noDataDouble,
      ),
      low_tide_time: orSentinel(record['low_tide_time'], Sentinels.noDataDate),
      low_tide_level: orNumSentinel(record['low_tide_level'], Sentinels.noDataDouble),
      low_tide_time2: orSentinel(record['low_tide_time2'], Sentinels.noDataDate),
      low_tide_level2: orNumSentinel(
        record['low_tide_level2'],
        Sentinels.noDataDouble,
      ),
      high_tide_time: orSentinel(record['high_tide_time'], Sentinels.noDataDate),
      high_tide_level: orNumSentinel(
        record['high_tide_level'],
        Sentinels.noDataDouble,
      ),
      high_tide_time2: orSentinel(record['high_tide_time2'], Sentinels.noDataDate),
      high_tide_level2: orNumSentinel(
        record['high_tide_level2'],
        Sentinels.noDataDouble,
      ),
      water_level: orNumSentinel(record['water_level'], Sentinels.noDataDouble),
      water_temp: orNumSentinel(record['water_temp'], Sentinels.noDataDouble),
      air_temp: orNumSentinel(record['air_temp'], Sentinels.noDataDouble),
      air_pressure: orNumSentinel(record['air_pressure'], Sentinels.noDataDouble),
      wind_speed: orNumSentinel(record['wind_speed'], Sentinels.noDataDouble),
      wind_gusts: orNumSentinel(record['wind_gusts'], Sentinels.noDataDouble),
      wind_direction: orSentinel(
        record['wind_direction'],
        Sentinels.noDataString,
      ),
    };

    const newId = (await db.fish_stats.add(stat as FishStat)) as number;

    // Save the photo if one came along (we already checked for its
    // presence above when computing image_name). iOS sends image_data
    // as a base64-encoded JPEG string; we decode and store it as a
    // Blob in fish_stat_images keyed by the fresh local id.
    if (hasPhoto) {
      try {
        const blob = base64ToBlob(imageData, 'image/jpeg');
        await db.fish_stat_images.put({
          fish_stat_id: newId,
          mime_type: 'image/jpeg',
          data: blob,
        });
      } catch (err) {
        console.warn('downloadFromServer: image decode failed', { newId, err });
      }
    }
  }

  /**
   * Retrieves the photo Blob for a catch, or null if none stored.
   */
  async getCatchPhoto(catchId: number): Promise<Blob | null> {
    const row = await db.fish_stat_images.get(catchId);
    return row?.data ?? null;
  }

  /**
   * Returns the distinct month+year buckets for which the user has
   * catches, sorted newest first. Powers the month/year picker on
   * My Catches.
   */
  async getMonthYearOptions(userId: number): Promise<MonthYearOption[]> {
    const all = await db.fish_stats
      .where('user_id')
      .equals(userId)
      .toArray();

    const buckets = new Map<string, MonthYearOption>();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];

    for (const s of all) {
      const y = parseInt(s.date_and_time.slice(0, 4), 10);
      const m = parseInt(s.date_and_time.slice(5, 7), 10);
      if (!Number.isFinite(y) || !Number.isFinite(m)) continue;
      const key = `${y}-${m}`;
      if (!buckets.has(key)) {
        buckets.set(key, {
          year: y,
          month: m,
          label: `${monthNames[m - 1]} ${y}`,
        });
      }
    }

    const options = Array.from(buckets.values());
    options.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });

    // Always include the current month even if empty, so the picker
    // has something to show for a brand-new user.
    const now = new Date();
    const curKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
    if (!buckets.has(curKey)) {
      options.unshift({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        label: `${monthNames[now.getMonth()]} ${now.getFullYear()}`,
      });
    }

    return options;
  }

  /**
   * Updates an existing catch in local storage and POSTs the new
   * values to the server. Same flow as createCatch but preserves the
   * existing record's id and remote_id.
   *
   * `photo` semantics:
   *   - `undefined` → leave the existing photo alone
   *   - `null`      → remove the existing photo
   *   - Blob        → replace with this new image
   */
  async updateCatch(
    catchId: number,
    input: Omit<NewCatchInput, 'photo'> & { photo: Blob | null | undefined },
  ): Promise<FishStat> {
    const existing = await db.fish_stats.get(catchId);
    if (!existing) throw new Error(`Catch ${catchId} not found`);

    const updates: Partial<FishStat> = {
      lat: input.lat,
      lon: input.lon,
      date_and_time: toMySqlDateTime(input.date_and_time),
      timezone: currentIanaTimezone(),
      fish_type_id: input.fish_type_id,
      fish_id: input.fish_id,
      fish_length_id: input.fish_length_id,
      fishing_technique_id: input.fishing_technique_id,
      fishing_technique_other: input.fishing_technique_other,
      is_update_needed: 1,
      last_update_failed: 0,
    };

    await db.fish_stats.update(catchId, updates);

    if (input.photo === null) {
      // User removed the photo.
      await db.fish_stat_images.delete(catchId);
      await db.fish_stats.update(catchId, { image_name: '' });
    } else if (input.photo) {
      // User replaced or added a photo.
      await db.fish_stat_images.put({
        fish_stat_id: catchId,
        mime_type: input.photo.type || 'image/jpeg',
        data: input.photo,
      });
      await db.fish_stats.update(catchId, {
        image_name: `MyFish_${catchId}.jpg`,
      });
    }
    // photo === undefined: leave the existing image alone.

    const updated = (await db.fish_stats.get(catchId)) as FishStat;
    await this.syncOne(updated).catch((err) =>
      console.warn('Update sync failed', err),
    );

    return (await db.fish_stats.get(catchId)) as FishStat;
  }

  /**
   * Deletes a catch. Removes the local record + its photo immediately,
   * then notifies the server. The server delete is a fire-and-forget
   * GET — even if the network call fails, the local copy is gone and
   * we don't try to retry. The server-side trash collector will clean
   * up orphaned records eventually.
   */
  async deleteCatch(catchId: number): Promise<void> {
    const stat = await db.fish_stats.get(catchId);

    // Local delete first, regardless of network state.
    await db.fish_stat_images.delete(catchId);
    await db.fish_stats.delete(catchId);

    // Best-effort server delete. We only call the API if the catch had
    // already synced to the server (remote_id != pending/no-data),
    // otherwise there's nothing to delete server-side.
    if (
      stat &&
      stat.remote_id !== Sentinels.pendingId &&
      stat.remote_id !== Sentinels.noDataId
    ) {
      try {
        const deviceId = await this.deviceIdService.getOrCreate();
        await firstValueFrom(
          this.api.deleteFishStat(
            stat.user_id,
            deviceId,
            stat.id,
            stat.remote_id,
          ),
        );
      } catch (err) {
        console.warn('Server delete failed (local already removed)', err);
      }
    }
  }

  /**
   * Look up a single catch by its local id. Returns undefined if not
   * present.
   */
  async getCatchById(catchId: number): Promise<FishStat | undefined> {
    return db.fish_stats.get(catchId);
  }

  /**
   * Fetches the "similar conditions" hints for a catch. Returns a
   * resolved list — each entry pairs the local FishStat we have stored
   * (looked up by remote_id) with the human-readable reason from the
   * server.
   *
   * Server returns a list of remote_ids and reasons; we resolve those
   * back to local records and silently drop any IDs we don't have
   * locally (could happen if the user cleared local data after the
   * server side recorded the catch). Network failures return an empty
   * list — UX is "no similar catches" rather than an error toast,
   * since this is supplementary info.
   */
  /**
   * Same as getSimilarCatches but takes raw coordinates and a time
   * instead of an existing catch. Used by the Wizard tab where the
   * "reference point" is wherever the user is standing right now,
   * not a previously-recorded catch.
   *
   * Returns the same `{ stat, reason }` shape so the consumer can
   * render the same UI components.
   */
  async getSimilarCatchesForReference(
    userId: number,
    lat: number,
    lon: number,
    when: Date,
    timezone: string,
  ): Promise<Array<{ stat: FishStat; reason: string }>> {
    try {
      const dateAndTime = toMySqlDateTime(when);
      const response = await firstValueFrom(
        this.api.getSimilarConditions(userId, lat, lon, dateAndTime, timezone),
      );

      const conditions = response?.conditions ?? [];
      if (conditions.length === 0) return [];

      const out: Array<{ stat: FishStat; reason: string }> = [];
      for (const c of conditions) {
        const remoteId = toNum(c.fish_stat_id);
        const matches = await db.fish_stats
          .where('remote_id')
          .equals(remoteId)
          .toArray();
        if (matches.length > 0) {
          out.push({ stat: matches[0]!, reason: c.similar_reason });
        }
      }
      return out;
    } catch (err) {
      console.warn('getSimilarCatchesForReference failed', err);
      return [];
    }
  }

  async getSimilarCatches(
    userId: number,
    referenceCatch: FishStat,
  ): Promise<Array<{ stat: FishStat; reason: string }>> {
    try {
      const response = await firstValueFrom(
        this.api.getSimilarConditions(
          userId,
          referenceCatch.lat,
          referenceCatch.lon,
          referenceCatch.date_and_time,
          referenceCatch.timezone,
        ),
      );

      const conditions = response?.conditions ?? [];
      if (conditions.length === 0) return [];

      // The server's similar_reason already mentions which catch each
      // one is — but we still need the local record to display fish
      // name, photo, etc. Resolve the remote_id → FishStat by querying
      // the remote_id index. Skip the reference catch itself.
      const out: Array<{ stat: FishStat; reason: string }> = [];
      for (const c of conditions) {
        const remoteId = toNum(c.fish_stat_id);
        if (remoteId === referenceCatch.remote_id) continue;
        const matches = await db.fish_stats
          .where('remote_id')
          .equals(remoteId)
          .toArray();
        if (matches.length > 0) {
          out.push({ stat: matches[0]!, reason: c.similar_reason });
        }
      }
      return out;
    } catch (err) {
      console.warn('getSimilarCatches failed', err);
      return [];
    }
  }

  /**
   * Finds all locally-created catches that still need server sync and
   * attempts to POST each one. Called on app startup after reconnect,
   * and manually from the refresh flow. Returns the count that
   * succeeded.
   */
  async syncPending(): Promise<number> {
    const pending = await db.fish_stats
      .where('is_update_needed')
      .equals(1)
      .toArray();
    let succeeded = 0;
    for (const stat of pending) {
      const ok = await this.syncOne(stat);
      if (ok) succeeded++;
    }
    return succeeded;
  }

  /**
   * Syncs a single catch. On success, merges server-populated fields
   * into the local record and clears the is_update_needed flag.
   * On failure, sets last_update_failed=1 so the UI can flag it.
   * Returns true iff the server reported success.
   */
  private async syncOne(stat: FishStat): Promise<boolean> {
    try {
      const form = await this.buildPostBody(stat);
      const response = await firstValueFrom(this.api.postFishStat(form));

      // The server's POST response shape (confirmed empirically against
      // www.saltwaterup.com/apiv1/fishStats.json):
      //   { "fishStats": [ { id, fish_id, ..., moon_*, tide_*, ... } ] }
      //
      // It's an array — usually with a single element on a single-record
      // POST — and there is NO top-level `status` field. Success is
      // signalled by the presence of a record with a numeric `id`. This
      // matches the iOS client's `loadFromRestElement` behavior, which
      // ignores any status field and just reads the record's id.
      //
      // Fields that aren't applicable for this catch come back as JSON
      // null (e.g. `low_tide_time2: null` when there's no second low
      // tide that day). Our toStr/toNum helpers translate null to
      // empty-string and 0 respectively, which falls under the "no
      // data" sentinel range — exactly the iOS client's convention.

      const fishStats = response?.fishStats;
      const records: Record<string, unknown>[] = Array.isArray(fishStats)
        ? (fishStats as Record<string, unknown>[])
        : fishStats && typeof fishStats === 'object'
          ? [fishStats as Record<string, unknown>]
          : [];

      const serverRecord = records[0];
      if (!serverRecord || serverRecord['id'] === undefined) {
        console.warn('[syncOne] no record returned', { response });
        await db.fish_stats.update(stat.id, { last_update_failed: 1 });
        return false;
      }

      const updates: Partial<FishStat> = {
        is_update_needed: 0,
        last_update_failed: 0,
        remote_id: toNum(serverRecord['id']),
      };

      for (const key of SERVER_POPULATED_FIELDS) {
        if (key === 'remote_id') continue; // handled above
        if (!(key in serverRecord)) continue;
        const raw = serverRecord[key];
        if (NUMERIC_SERVER_FIELDS.has(key)) {
          (updates as Record<string, unknown>)[key] = toNum(raw);
        } else {
          (updates as Record<string, unknown>)[key] = toStr(raw);
        }
      }

      await db.fish_stats.update(stat.id, updates);
      return true;
    } catch (err) {
      console.warn('syncOne failed', err);
      await db.fish_stats.update(stat.id, { last_update_failed: 1 });
      return false;
    }
  }

  private async buildPostBody(
    stat: FishStat,
  ): Promise<Record<string, string | undefined>> {
    const body: Record<string, string | undefined> = {
      name: stat.name,
      user_id: String(stat.user_id),
      device_id: stat.device_id,
      local_id: String(stat.id),
      lat: stat.lat.toFixed(4),
      lon: stat.lon.toFixed(4),
      date_and_time: stat.date_and_time,
      timezone: stat.timezone,
      fish_type_id: String(stat.fish_type_id),
      fish_id: String(stat.fish_id),
      fish_length_id: String(stat.fish_length_id),
      fishing_technique_id: String(stat.fishing_technique_id),
      fishing_technique_other: stat.fishing_technique_other,
      need_noaa_data: this.hasNoaaData(stat) ? 'false' : 'true',
    };

    if (
      stat.remote_id !== Sentinels.pendingId &&
      stat.remote_id !== Sentinels.noDataId
    ) {
      body['remote_id'] = String(stat.remote_id);
    }

    const photo = await this.getCatchPhoto(stat.id);
    if (photo) {
      body['image_data'] = await this.photos.blobToBase64(photo);
    }

    return body;
  }

  private hasNoaaData(stat: FishStat): boolean {
    // iOS FishStat.hasNoaaData returns YES if noaa_station_number is
    // populated (not pending/no-data). We do the same — the server
    // uses this flag to avoid repeating an expensive NOAA lookup when
    // conditions have already been fetched for this catch.
    return (
      stat.noaa_station_number !== Sentinels.pendingInt &&
      stat.noaa_station_number !== Sentinels.noDataInt
    );
  }
}

/**
 * Fields on FishStat that are populated by the server. Used by syncOne
 * to decide which fields to overwrite from a POST response.
 */
const SERVER_POPULATED_FIELDS = [
  'remote_id',
  'moonrise',
  'moonset',
  'sunrise',
  'sunset',
  'moon_phase',
  'moon_percent_illuminated',
  'moon_position',
  'major_feed_time_start',
  'major_feed_time_end',
  'major_feed_time_start2',
  'major_feed_time_end2',
  'minor_feed_time_start',
  'minor_feed_time_end',
  'minor_feed_time_start2',
  'minor_feed_time_end2',
  'noaa_station_name',
  'noaa_station_number',
  'noaa_station_distance',
  'low_tide_time',
  'low_tide_level',
  'low_tide_time2',
  'low_tide_level2',
  'high_tide_time',
  'high_tide_level',
  'high_tide_time2',
  'high_tide_level2',
  'water_level',
  'water_temp',
  'air_temp',
  'air_pressure',
  'wind_speed',
  'wind_gusts',
  'wind_direction',
] as const;

const NUMERIC_SERVER_FIELDS = new Set<string>([
  'remote_id',
  'moon_percent_illuminated',
  'moon_position',
  'noaa_station_number',
  'noaa_station_distance',
  'low_tide_level',
  'low_tide_level2',
  'high_tide_level',
  'high_tide_level2',
  'water_level',
  'water_temp',
  'air_temp',
  'air_pressure',
  'wind_speed',
  'wind_gusts',
]);
